import { BaseInterior } from './BaseInterior';
import { socketManager, PlantBatchInfo } from '../socket/SocketManager';

// ── Color palette ──────────────────────────────────────────────────────
const COLORS = {
  floorLeft: 0x2a2a2a,
  floorRight: 0x1a2a1a,
  greenhouseBorder: 0x3a5a3a,
  uvLight: 0x9b59b6,
  pot: 0x8b4513,
  potRim: 0xa0522d,
  soil: 0x3e2723,
  stem: 0x2e7d32,
  leaf: 0x4caf50,
  budFlowering: 0xff9800,
  budHarvest: 0xffd700,
  dead: 0x5d4037,
  waterPipe: 0x2196f3,
  ventilation: 0x666666,
  cardBg: 0x1e1e1e,
  wall: 0x3b4b3b,
};

const STAGE_COLORS: Record<string, number> = {
  SEED: 0x8bc34a,
  VEGETATIVE: 0x4caf50,
  FLOWERING: 0xff9800,
  HARVEST: 0xffd700,
  DEAD: 0x666666,
};

const STAGE_NAMES: Record<string, string> = {
  SEED: 'Graine',
  VEGETATIVE: 'Vegetatif',
  FLOWERING: 'Floraison',
  HARVEST: 'Pret a recolter',
  DEAD: 'Mort',
};

const OPTIMAL = { humidity: 60, temperature: 24, lightHours: 18 };

export class GrowRoomInterior extends BaseInterior {
  private showHistory = false;

  render(): void {
    const { width, height } = this.scene.scale;
    const margin = 40;
    const innerW = width - margin * 2;
    const innerH = height - margin * 2;

    this.drawSplitRoom(margin, innerW, innerH, width, height);
    this.addExitButton();
    this.addTitle(this.building.name + ' - Labo');

    const gi = this.stateData?.growRoomInfo;

    if (!gi) {
      this.addLabel(width / 2, height / 2 - 20, 'Grow room pas encore configuree.', '#ff9800', '16px')
        .setOrigin(0.5);
      this.addButton(width / 2, height / 2 + 30, '[ Creer la Grow Room ]', '#4caf50', () => {
        socketManager.sendAction('CREATE_GROW_ROOM', { buildingId: this.building.id });
        this.refreshAfterAction();
      });
      return;
    }

    // ── Zone boundaries ──────────────────────────────────────────────
    const panelX = margin + 14;
    const panelW = Math.floor(innerW * 0.40);
    const ghX = margin + panelW + 24;
    const ghW = innerW - panelW - 36;
    const contentTop = 82;

    // ── RIGHT: Greenhouse or History ──────────────────────────────────
    const ghH = innerH - (contentTop - margin) - 20;
    if (this.showHistory) {
      this.drawHistory(ghX, contentTop, ghW, ghH, gi);
    } else {
      this.drawGreenhouse(ghX, contentTop, ghW, ghH, gi);
    }

    // Toggle button top-right of greenhouse
    const toggleLabel = this.showHistory ? '  Serre  ' : '  Historique  ';
    const toggleColor = this.showHistory ? '#4caf50' : '#aaaaaa';
    this.addButton(ghX + ghW - 60, contentTop - 14, toggleLabel, toggleColor, () => {
      this.showHistory = !this.showHistory;
      this.destroy();
      this.graphics = this.scene.add.graphics();
      this.render();
    });

    // ── LEFT: Info panel ─────────────────────────────────────────────
    // Calculate available height to distribute sections proportionally
    // Only show active batches in the left panel (DEAD go to history)
    const activeBatches = gi.batches.filter(b => b.stage !== 'DEAD');

    const panelBottom = height - margin - 12;
    const availableH = panelBottom - contentTop;

    // Reserve space for actions at the bottom (3 buttons max = ~110px)
    const actionCount = (gi.currentPlants < gi.capacity ? 2 : 0) + (activeBatches.some(b => b.stage === 'HARVEST') ? 1 : 0);
    const actionsH = Math.max(40, actionCount * 34 + 10);

    // Remaining space is split between batch cards and config
    const remainingH = availableH - actionsH;
    const hasConfig = activeBatches.length > 0;
    const configH = hasConfig ? Math.min(160, Math.floor(remainingH * 0.42)) : 0;
    const cardsH = remainingH - configH;
    const batchCount = activeBatches.length;
    const cardGap = 10;
    const maxCardH = batchCount > 0 ? Math.floor((cardsH - batchCount * cardGap) / Math.max(1, batchCount)) : 0;
    const cardH = Math.min(110, Math.max(70, maxCardH));

    let y = contentTop;

    // Batch info cards (active only)
    if (batchCount === 0) {
      this.addLabel(panelX + 10, y, 'Aucun plant en culture.', '#666666', '13px');
      y += 40;
    } else {
      for (const batch of activeBatches) {
        y = this.renderBatchCard(batch, panelX, y, panelW, cardH);
        y += cardGap;
      }
    }

    // Config gauges
    const activeBatch = activeBatches.find(b => b.stage !== 'HARVEST') || activeBatches[0];
    if (activeBatch) {
      const gaugeH = Math.min(60, Math.max(35, configH - 80));
      y = this.renderConfigGauges(activeBatch, gi, panelX, y, panelW, gaugeH);
      y += 12;
    }

    // Actions — positioned at the bottom of the panel
    const actionsY = Math.max(y, panelBottom - actionsH);
    this.renderActions(gi, panelX, actionsY, panelW, ghX, ghW, height, margin);
  }

  // ════════════════════════════════════════════════════════════════════
  //  Room drawing
  // ════════════════════════════════════════════════════════════════════
  private drawSplitRoom(
    margin: number, innerW: number, innerH: number,
    width: number, height: number,
  ): void {
    const panelW = Math.floor(innerW * 0.40);

    this.graphics.fillStyle(COLORS.floorLeft, 1);
    this.graphics.fillRect(margin, margin, panelW + 20, innerH);

    this.graphics.fillStyle(COLORS.floorRight, 1);
    this.graphics.fillRect(margin + panelW + 20, margin, innerW - panelW - 20, innerH);

    this.graphics.fillStyle(COLORS.wall, 1);
    this.graphics.fillRect(margin, margin, innerW, 8);
    this.graphics.fillRect(margin, height - margin - 8, innerW, 8);
    this.graphics.fillRect(margin, margin, 8, innerH);
    this.graphics.fillRect(width - margin - 8, margin, 8, innerH);

    this.graphics.lineStyle(2, 0x444444, 0.5);
    this.graphics.beginPath();
    this.graphics.moveTo(margin + panelW + 20, margin + 8);
    this.graphics.lineTo(margin + panelW + 20, height - margin - 8);
    this.graphics.strokePath();
  }

  // ════════════════════════════════════════════════════════════════════
  //  Greenhouse (right)
  // ════════════════════════════════════════════════════════════════════
  private drawGreenhouse(
    x: number, y: number, w: number, h: number,
    gi: { batches: PlantBatchInfo[]; capacity: number; currentPlants: number },
  ): void {
    this.graphics.fillStyle(0x141e14, 0.8);
    this.graphics.fillRoundedRect(x, y, w, h, 6);
    this.graphics.lineStyle(2, COLORS.greenhouseBorder, 0.7);
    this.graphics.strokeRoundedRect(x, y, w, h, 6);

    // UV lights
    const lightY = y + 14;
    const numLights = 4;
    const lightPad = 24;
    const lightW = (w - lightPad * 2 - (numLights - 1) * 10) / numLights;

    for (let i = 0; i < numLights; i++) {
      const lx = x + lightPad + i * (lightW + 10);
      this.graphics.fillStyle(COLORS.uvLight, 0.06);
      this.graphics.fillRect(lx - 6, lightY - 8, lightW + 12, 24);
      this.graphics.fillStyle(COLORS.uvLight, 0.7);
      this.graphics.fillRect(lx, lightY, lightW, 6);
      this.graphics.fillStyle(COLORS.uvLight, 0.02);
      this.graphics.fillRect(lx + 4, lightY + 6, lightW - 8, 50);
    }

    // Ventilation
    const ventX = x + w - 52;
    const ventY = y + 8;
    this.graphics.fillStyle(COLORS.ventilation, 0.6);
    this.graphics.fillRect(ventX, ventY, 42, 22);
    this.graphics.lineStyle(1, 0x888888, 0.4);
    for (let i = 0; i < 5; i++) {
      this.graphics.beginPath();
      this.graphics.moveTo(ventX + 4, ventY + 4 + i * 4);
      this.graphics.lineTo(ventX + 38, ventY + 4 + i * 4);
      this.graphics.strokePath();
    }

    // Water pipe
    const pipeY = y + h - 16;
    this.graphics.lineStyle(3, COLORS.waterPipe, 0.3);
    this.graphics.beginPath();
    this.graphics.moveTo(x + 10, pipeY);
    this.graphics.lineTo(x + w - 10, pipeY);
    this.graphics.strokePath();
    for (let px = x + 50; px < x + w - 20; px += 70) {
      this.graphics.fillStyle(COLORS.waterPipe, 0.45);
      this.graphics.fillCircle(px, pipeY, 3);
    }

    // Plant grid
    const cols = 4;
    const rows = 5;
    const cellW = 60;
    const cellH = 76;
    const gridW = cols * cellW;
    const gridStartX = x + Math.floor((w - gridW) / 2);
    const gridStartY = y + 44;

    const plants: { stage: string; health: number }[] = [];
    for (const batch of gi.batches) {
      for (let i = 0; i < batch.quantity; i++) {
        plants.push({ stage: batch.stage, health: batch.health });
      }
    }

    const totalSlots = cols * rows;
    for (let slot = 0; slot < Math.min(totalSlots, gi.capacity); slot++) {
      const col = slot % cols;
      const row = Math.floor(slot / cols);
      const cx = gridStartX + col * cellW + cellW / 2;
      const cy = gridStartY + row * cellH + cellH - 10;

      if (slot < plants.length) {
        this.graphics.fillStyle(COLORS.soil, 0.35);
        this.graphics.fillRect(cx - cellW / 2 + 4, cy - 2, cellW - 8, 4);
        this.drawPlant(cx, cy, plants[slot].stage, plants[slot].health, 1.0);
      } else {
        this.drawEmptyPot(cx, cy);
      }
    }

    // Plant count
    this.addLabel(x + 14, y + h - 34, `Plants: ${gi.currentPlants}/${gi.capacity}`, '#8bc34a', '12px');
  }

  // ════════════════════════════════════════════════════════════════════
  //  History view (replaces greenhouse)
  // ════════════════════════════════════════════════════════════════════
  private drawHistory(
    x: number, y: number, w: number, h: number,
    gi: { batches: PlantBatchInfo[]; capacity: number; currentPlants: number },
  ): void {
    // Background
    this.graphics.fillStyle(0x1a1a1a, 0.9);
    this.graphics.fillRoundedRect(x, y, w, h, 6);
    this.graphics.lineStyle(2, 0x444444, 0.5);
    this.graphics.strokeRoundedRect(x, y, w, h, 6);

    this.addLabel(x + 14, y + 12, 'HISTORIQUE', '#ff6b35', '14px');

    // Filter: only DEAD batches (harvested or died)
    const history = gi.batches.filter(b => b.stage === 'DEAD');

    if (history.length === 0) {
      this.addLabel(x + w / 2, y + h / 2, 'Aucun historique.', '#666666', '13px').setOrigin(0.5);
      return;
    }

    // Sort by endedAt descending (most recent first)
    const sorted = [...history].sort((a, b) => {
      if (!a.endedAt && !b.endedAt) return 0;
      if (!a.endedAt) return 1;
      if (!b.endedAt) return -1;
      return new Date(b.endedAt).getTime() - new Date(a.endedAt).getTime();
    });

    let hy = y + 38;
    const rowH = 52;
    const maxRows = Math.floor((h - 50) / rowH);

    for (let i = 0; i < Math.min(sorted.length, maxRows); i++) {
      const batch = sorted[i];
      const isHarvested = batch.deathReason === 'HARVESTED';
      const rowColor = isHarvested ? 0x1a2a1a : 0x2a1a1a;
      const borderColor = isHarvested ? 0x4caf50 : 0xff4444;

      // Row background
      this.graphics.fillStyle(rowColor, 0.7);
      this.graphics.fillRoundedRect(x + 10, hy, w - 20, rowH - 6, 4);
      this.graphics.lineStyle(1, borderColor, 0.4);
      this.graphics.strokeRoundedRect(x + 10, hy, w - 20, rowH - 6, 4);

      // Icon
      const iconX = x + 26;
      const iconY = hy + (rowH - 6) / 2;
      if (isHarvested) {
        // Green checkmark
        this.graphics.lineStyle(3, 0x4caf50, 0.9);
        this.graphics.beginPath();
        this.graphics.moveTo(iconX - 6, iconY);
        this.graphics.lineTo(iconX - 2, iconY + 5);
        this.graphics.lineTo(iconX + 6, iconY - 5);
        this.graphics.strokePath();
      } else {
        // Red X
        this.graphics.lineStyle(3, 0xff4444, 0.9);
        this.graphics.beginPath();
        this.graphics.moveTo(iconX - 5, iconY - 5);
        this.graphics.lineTo(iconX + 5, iconY + 5);
        this.graphics.strokePath();
        this.graphics.beginPath();
        this.graphics.moveTo(iconX + 5, iconY - 5);
        this.graphics.lineTo(iconX - 5, iconY + 5);
        this.graphics.strokePath();
      }

      // Info text
      const textX = x + 44;
      const statusText = isHarvested ? 'Recolte' : 'Mort';
      const statusColor = isHarvested ? '#4caf50' : '#ff4444';

      this.addLabel(textX, hy + 6, `${batch.strain} x${batch.quantity} — ${statusText}`, statusColor, '12px');

      if (isHarvested) {
        this.addLabel(textX, hy + 24, `Rendement: ${batch.harvestedYield}g`, '#69db7c', '11px');
      } else {
        this.addLabel(textX, hy + 24, `Sante finale: ${Math.round(batch.health)}%`, '#ff8888', '11px');
      }

      // Date
      if (batch.endedAt) {
        const date = new Date(batch.endedAt);
        const timeStr = `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
        this.addLabel(x + w - 70, hy + 14, timeStr, '#888888', '11px');
      }

      hy += rowH;
    }

    // Summary at bottom
    const totalHarvested = sorted.filter(b => b.deathReason === 'HARVESTED').reduce((sum, b) => sum + b.harvestedYield, 0);
    const totalDead = sorted.filter(b => b.deathReason !== 'HARVESTED').length;
    const summaryY = y + h - 30;
    this.addLabel(x + 14, summaryY, `Total recolte: ${totalHarvested}g`, '#69db7c', '12px');
    this.addLabel(x + w / 2, summaryY, `Pertes: ${totalDead}`, '#ff8888', '12px');
  }

  // ════════════════════════════════════════════════════════════════════
  //  Plant drawing
  // ════════════════════════════════════════════════════════════════════
  private drawPlant(cx: number, baseY: number, stage: string, health: number, scale: number): void {
    const s = scale;
    const alpha = stage === 'DEAD' ? 0.4 : 1.0;

    // Pot
    this.graphics.fillStyle(COLORS.pot, alpha);
    this.graphics.beginPath();
    this.graphics.moveTo(cx - 14 * s, baseY - 16 * s);
    this.graphics.lineTo(cx + 14 * s, baseY - 16 * s);
    this.graphics.lineTo(cx + 11 * s, baseY);
    this.graphics.lineTo(cx - 11 * s, baseY);
    this.graphics.closePath();
    this.graphics.fillPath();

    this.graphics.fillStyle(COLORS.potRim, alpha);
    this.graphics.fillRect(cx - 15 * s, baseY - 18 * s, 30 * s, 4 * s);

    this.graphics.fillStyle(COLORS.soil, alpha);
    this.graphics.fillRect(cx - 12 * s, baseY - 16 * s, 24 * s, 4 * s);

    const potTop = baseY - 16 * s;

    if (stage === 'SEED') {
      this.graphics.lineStyle(2 * s, COLORS.stem, alpha);
      this.graphics.beginPath();
      this.graphics.moveTo(cx, potTop);
      this.graphics.lineTo(cx, potTop - 8 * s);
      this.graphics.strokePath();
      this.graphics.fillStyle(COLORS.leaf, alpha);
      this.graphics.fillCircle(cx - 2 * s, potTop - 9 * s, 2.5 * s);
      this.graphics.fillCircle(cx + 2 * s, potTop - 9 * s, 2.5 * s);
    } else if (stage === 'VEGETATIVE') {
      this.drawVegetativePlant(cx, potTop, s, alpha);
    } else if (stage === 'FLOWERING') {
      this.drawFloweringPlant(cx, potTop, s, alpha);
    } else if (stage === 'HARVEST') {
      this.drawHarvestPlant(cx, potTop, s, alpha);
    } else {
      this.drawDeadPlant(cx, potTop, s);
    }
  }

  private drawVegetativePlant(cx: number, soilY: number, s: number, alpha: number): void {
    const stemH = 24 * s;
    this.graphics.lineStyle(2.5 * s, COLORS.stem, alpha);
    this.graphics.beginPath();
    this.graphics.moveTo(cx, soilY);
    this.graphics.lineTo(cx + 1 * s, soilY - stemH * 0.5);
    this.graphics.lineTo(cx, soilY - stemH);
    this.graphics.strokePath();

    const branches = [
      { y: soilY - stemH * 0.4, len: 10 },
      { y: soilY - stemH * 0.75, len: 8 },
    ];
    for (const bp of branches) {
      this.graphics.lineStyle(1.5 * s, COLORS.stem, alpha * 0.8);
      this.graphics.beginPath();
      this.graphics.moveTo(cx, bp.y);
      this.graphics.lineTo(cx - bp.len * s, bp.y - 3 * s);
      this.graphics.strokePath();
      this.drawLeaf(cx - bp.len * s, bp.y - 3 * s, -1, s, alpha);

      this.graphics.beginPath();
      this.graphics.moveTo(cx, bp.y);
      this.graphics.lineTo(cx + bp.len * s, bp.y - 3 * s);
      this.graphics.strokePath();
      this.drawLeaf(cx + bp.len * s, bp.y - 3 * s, 1, s, alpha);
    }
    this.drawLeaf(cx - 2 * s, soilY - stemH - 2 * s, -1, s * 0.8, alpha);
    this.drawLeaf(cx + 2 * s, soilY - stemH - 2 * s, 1, s * 0.8, alpha);
  }

  private drawFloweringPlant(cx: number, soilY: number, s: number, alpha: number): void {
    const stemH = 32 * s;
    this.graphics.lineStyle(3 * s, COLORS.stem, alpha);
    this.graphics.beginPath();
    this.graphics.moveTo(cx, soilY);
    this.graphics.lineTo(cx + 1 * s, soilY - stemH * 0.3);
    this.graphics.lineTo(cx - 0.5 * s, soilY - stemH * 0.6);
    this.graphics.lineTo(cx, soilY - stemH);
    this.graphics.strokePath();

    const branches = [
      { y: soilY - stemH * 0.3, len: 12 },
      { y: soilY - stemH * 0.55, len: 10 },
      { y: soilY - stemH * 0.8, len: 7 },
    ];
    for (const bp of branches) {
      this.graphics.lineStyle(1.5 * s, COLORS.stem, alpha * 0.8);
      this.graphics.beginPath();
      this.graphics.moveTo(cx, bp.y);
      this.graphics.lineTo(cx - bp.len * s, bp.y - 4 * s);
      this.graphics.strokePath();
      this.drawLeaf(cx - bp.len * s + 2 * s, bp.y - 4 * s, -1, s, alpha);
      this.graphics.fillStyle(COLORS.budFlowering, alpha);
      this.graphics.fillCircle(cx - bp.len * s, bp.y - 5 * s, 2.5 * s);

      this.graphics.beginPath();
      this.graphics.moveTo(cx, bp.y);
      this.graphics.lineTo(cx + bp.len * s, bp.y - 4 * s);
      this.graphics.strokePath();
      this.drawLeaf(cx + bp.len * s - 2 * s, bp.y - 4 * s, 1, s, alpha);
      this.graphics.fillStyle(COLORS.budFlowering, alpha);
      this.graphics.fillCircle(cx + bp.len * s, bp.y - 5 * s, 2.5 * s);
    }
    this.drawLeaf(cx - 3 * s, soilY - stemH - 2 * s, -1, s * 0.7, alpha);
    this.drawLeaf(cx + 3 * s, soilY - stemH - 2 * s, 1, s * 0.7, alpha);
    this.graphics.fillStyle(COLORS.budFlowering, alpha);
    this.graphics.fillCircle(cx, soilY - stemH - 3 * s, 3 * s);
  }

  private drawHarvestPlant(cx: number, soilY: number, s: number, alpha: number): void {
    const stemH = 38 * s;

    this.graphics.fillStyle(COLORS.budHarvest, 0.08);
    this.graphics.fillCircle(cx, soilY - stemH * 0.5, 28 * s);

    this.graphics.lineStyle(3.5 * s, COLORS.stem, alpha);
    this.graphics.beginPath();
    this.graphics.moveTo(cx, soilY);
    this.graphics.lineTo(cx + 1.5 * s, soilY - stemH * 0.25);
    this.graphics.lineTo(cx - 1 * s, soilY - stemH * 0.5);
    this.graphics.lineTo(cx + 0.5 * s, soilY - stemH * 0.75);
    this.graphics.lineTo(cx, soilY - stemH);
    this.graphics.strokePath();

    const branches = [
      { y: soilY - stemH * 0.2, len: 14 },
      { y: soilY - stemH * 0.4, len: 12 },
      { y: soilY - stemH * 0.6, len: 10 },
      { y: soilY - stemH * 0.8, len: 7 },
    ];
    for (const bp of branches) {
      this.graphics.lineStyle(2 * s, COLORS.stem, alpha * 0.8);
      this.graphics.beginPath();
      this.graphics.moveTo(cx, bp.y);
      this.graphics.lineTo(cx - bp.len * s, bp.y - 5 * s);
      this.graphics.strokePath();
      this.drawLeaf(cx - bp.len * s + 3 * s, bp.y - 5 * s, -1, s * 1.1, alpha);
      this.graphics.fillStyle(COLORS.budHarvest, alpha);
      this.graphics.fillCircle(cx - bp.len * s, bp.y - 6 * s, 3.5 * s);
      this.graphics.fillStyle(COLORS.budHarvest, alpha * 0.4);
      this.graphics.fillCircle(cx - bp.len * s, bp.y - 6 * s, 5 * s);

      this.graphics.beginPath();
      this.graphics.moveTo(cx, bp.y);
      this.graphics.lineTo(cx + bp.len * s, bp.y - 5 * s);
      this.graphics.strokePath();
      this.drawLeaf(cx + bp.len * s - 3 * s, bp.y - 5 * s, 1, s * 1.1, alpha);
      this.graphics.fillStyle(COLORS.budHarvest, alpha);
      this.graphics.fillCircle(cx + bp.len * s, bp.y - 6 * s, 3.5 * s);
      this.graphics.fillStyle(COLORS.budHarvest, alpha * 0.4);
      this.graphics.fillCircle(cx + bp.len * s, bp.y - 6 * s, 5 * s);
    }

    this.drawLeaf(cx - 4 * s, soilY - stemH - 2 * s, -1, s * 0.9, alpha);
    this.drawLeaf(cx + 4 * s, soilY - stemH - 2 * s, 1, s * 0.9, alpha);
    this.graphics.fillStyle(COLORS.budHarvest, 0.25);
    this.graphics.fillCircle(cx, soilY - stemH - 3 * s, 7 * s);
    this.graphics.fillStyle(COLORS.budHarvest, alpha);
    this.graphics.fillCircle(cx, soilY - stemH - 3 * s, 4 * s);
  }

  private drawDeadPlant(cx: number, soilY: number, s: number): void {
    const stemH = 20 * s;
    this.graphics.lineStyle(2 * s, COLORS.dead, 0.4);
    this.graphics.beginPath();
    this.graphics.moveTo(cx, soilY);
    this.graphics.lineTo(cx + 2 * s, soilY - stemH * 0.5);
    this.graphics.lineTo(cx + 5 * s, soilY - stemH * 0.8);
    this.graphics.lineTo(cx + 8 * s, soilY - stemH);
    this.graphics.strokePath();

    this.graphics.lineStyle(1 * s, COLORS.dead, 0.3);
    this.graphics.beginPath();
    this.graphics.moveTo(cx + 2 * s, soilY - stemH * 0.4);
    this.graphics.lineTo(cx - 6 * s, soilY - stemH * 0.2);
    this.graphics.strokePath();
    this.graphics.beginPath();
    this.graphics.moveTo(cx + 5 * s, soilY - stemH * 0.7);
    this.graphics.lineTo(cx + 12 * s, soilY - stemH * 0.5);
    this.graphics.strokePath();
  }

  private drawLeaf(x: number, y: number, dir: number, s: number, alpha: number): void {
    const lw = 6 * s;
    const lh = 3 * s;
    this.graphics.fillStyle(COLORS.leaf, alpha * 0.85);

    this.graphics.beginPath();
    this.graphics.moveTo(x, y);
    this.graphics.lineTo(x + lw * dir, y - lh * 0.3);
    this.graphics.lineTo(x + lw * 0.6 * dir, y - lh);
    this.graphics.lineTo(x + lw * 0.2 * dir, y - lh * 0.8);
    this.graphics.closePath();
    this.graphics.fillPath();

    this.graphics.beginPath();
    this.graphics.moveTo(x, y);
    this.graphics.lineTo(x + lw * dir, y - lh * 0.3);
    this.graphics.lineTo(x + lw * 0.6 * dir, y + lh * 0.5);
    this.graphics.lineTo(x + lw * 0.2 * dir, y + lh * 0.3);
    this.graphics.closePath();
    this.graphics.fillPath();

    this.graphics.lineStyle(0.5 * s, COLORS.stem, alpha * 0.5);
    this.graphics.beginPath();
    this.graphics.moveTo(x, y);
    this.graphics.lineTo(x + lw * 0.8 * dir, y - lh * 0.1);
    this.graphics.strokePath();
  }

  private drawEmptyPot(cx: number, baseY: number): void {
    this.graphics.lineStyle(1, COLORS.pot, 0.2);
    this.graphics.beginPath();
    this.graphics.moveTo(cx - 14, baseY - 16);
    this.graphics.lineTo(cx + 14, baseY - 16);
    this.graphics.lineTo(cx + 11, baseY);
    this.graphics.lineTo(cx - 11, baseY);
    this.graphics.closePath();
    this.graphics.strokePath();
    this.graphics.strokeRect(cx - 15, baseY - 18, 30, 4);
  }

  // ════════════════════════════════════════════════════════════════════
  //  Batch info card
  // ════════════════════════════════════════════════════════════════════
  private renderBatchCard(batch: PlantBatchInfo, x: number, y: number, panelW: number, cardH: number = 110): number {
    const cardW = panelW - 20;
    const compact = cardH < 90;
    const stageColor = STAGE_COLORS[batch.stage] ?? 0x888888;
    const stageName = STAGE_NAMES[batch.stage] ?? batch.stage;
    const colorHex = stageColor === 0x888888 ? '#888888' : `#${stageColor.toString(16).padStart(6, '0')}`;

    // Card background
    this.graphics.fillStyle(COLORS.cardBg, 0.9);
    this.graphics.fillRoundedRect(x, y, cardW, cardH, 6);
    this.graphics.lineStyle(2, stageColor, 0.5);
    this.graphics.strokeRoundedRect(x, y, cardW, cardH, 6);

    const barLeft = x + 14;
    const barWidth = cardW - 70;

    if (compact) {
      // Compact: single line title + health + yield
      this.graphics.fillStyle(stageColor, 1);
      this.graphics.fillCircle(x + 14, y + 14, 4);
      this.addLabel(x + 24, y + 7, `${batch.strain} x${batch.quantity} — ${stageName}`, '#ffffff', '11px');

      const hy = y + 26;
      this.addLabel(barLeft, hy, 'Sante', '#999999', '10px');
      const healthColor = batch.health > 60 ? 0x4caf50 : batch.health > 30 ? 0xff9800 : 0xff4444;
      this.drawProgressBar(barLeft + 42, hy + 2, barWidth - 42, 8, batch.health / 100, 0x333333, healthColor);
      this.addLabel(x + cardW - 40, hy, `${Math.round(batch.health)}%`, '#dddddd', '10px');

      if (batch.ticksRequired > 0) {
        const py = y + 42;
        const progress = batch.ticksInStage / batch.ticksRequired;
        this.addLabel(barLeft, py, 'Progres', '#999999', '10px');
        this.drawProgressBar(barLeft + 42, py + 2, barWidth - 42, 8, progress, 0x333333, 0x3a6bc4);
        this.addLabel(x + cardW - 40, py, `${Math.round(progress * 100)}%`, '#dddddd', '10px');
      }

      this.addLabel(barLeft, y + cardH - 20, `~${batch.estimatedYield}g`, '#69db7c', '11px');
    } else {
      // Normal: two-line title + bars + yield
      this.graphics.fillStyle(stageColor, 1);
      this.graphics.fillCircle(x + 16, y + 18, 5);
      this.addLabel(x + 28, y + 10, `${batch.strain} x${batch.quantity}`, '#ffffff', '13px');
      this.addLabel(x + 28, y + 28, stageName, colorHex, '11px');

      const hy = y + 50;
      this.addLabel(barLeft, hy, 'Sante', '#999999', '11px');
      const healthColor = batch.health > 60 ? 0x4caf50 : batch.health > 30 ? 0xff9800 : 0xff4444;
      this.drawProgressBar(barLeft + 50, hy + 3, barWidth - 50, 10, batch.health / 100, 0x333333, healthColor);
      this.addLabel(x + cardW - 44, hy, `${Math.round(batch.health)}%`, '#dddddd', '11px');

      if (batch.ticksRequired > 0) {
        const py = y + 68;
        const progress = batch.ticksInStage / batch.ticksRequired;
        this.addLabel(barLeft, py, 'Progres', '#999999', '11px');
        this.drawProgressBar(barLeft + 50, py + 3, barWidth - 50, 10, progress, 0x333333, 0x3a6bc4);
        this.addLabel(x + cardW - 44, py, `${Math.round(progress * 100)}%`, '#dddddd', '11px');
      }

      this.addLabel(barLeft, y + 88, `Rendement: ~${batch.estimatedYield}g`, '#69db7c', '12px');
    }

    return y + cardH;
  }

  // ════════════════════════════════════════════════════════════════════
  //  Config gauges
  // ════════════════════════════════════════════════════════════════════
  private renderConfigGauges(
    batch: PlantBatchInfo,
    gi: { batches: PlantBatchInfo[]; id: number },
    panelX: number, startY: number, panelW: number,
    gaugeH: number = 60,
  ): number {
    const y = startY + 4;
    this.addLabel(panelX + 10, y, 'CONFIGURATION', '#ff6b35', '12px');

    const gaugeW = 24;
    const sectionTop = y + 22;

    // 3 gauges spaced evenly across the panel
    const totalGaugeArea = panelW - 30;
    const colW = Math.floor(totalGaugeArea / 3);

    const configs = [
      { label: 'H%', value: batch.targetHumidity, current: batch.currentHumidity, optimal: OPTIMAL.humidity, max: 100, unit: '%', key: 'humidity' as const, step: 5 },
      { label: 'T°C', value: batch.targetTemperature, current: batch.currentTemperature, optimal: OPTIMAL.temperature, max: 50, unit: 'C', key: 'temperature' as const, step: 2 },
      { label: 'Lux', value: batch.targetLightHours, current: batch.currentLightHours, optimal: OPTIMAL.lightHours, max: 24, unit: 'h', key: 'lightHours' as const, step: 2 },
    ];

    for (let i = 0; i < configs.length; i++) {
      const cfg = configs[i];
      const colX = panelX + 14 + i * colW;
      const barX = colX + Math.floor((colW - gaugeW) / 2);
      const barY = sectionTop + 20;

      // Label
      this.addLabel(colX + 2, sectionTop, cfg.label, '#bbbbbb', '11px');

      // + button above gauge
      if (batch.stage !== 'HARVEST' && batch.stage !== 'DEAD') {
        this.addButton(barX + gaugeW / 2, barY - 6, ' + ', '#4caf50', () => {
          for (const b of gi.batches) {
            if (b.stage !== 'HARVEST' && b.stage !== 'DEAD') {
              const p: Record<string, number> = { batchId: b.id };
              p[cfg.key] = Math.min(cfg.max, cfg.value + cfg.step);
              socketManager.sendAction('CONFIGURE_GROW', p);
            }
          }
          this.refreshAfterAction();
        });
      }

      // Gauge background
      this.graphics.fillStyle(0x333333, 1);
      this.graphics.fillRoundedRect(barX, barY, gaugeW, gaugeH, 4);

      // Color based on deviation
      const deviation = Math.abs(cfg.value - cfg.optimal) / cfg.optimal;
      const gaugeColor = deviation <= 0.10 ? 0x4caf50 : deviation <= 0.25 ? 0xff9800 : 0xff4444;

      // Fill
      const fillH = (cfg.value / cfg.max) * gaugeH;
      this.graphics.fillStyle(gaugeColor, 0.8);
      this.graphics.fillRoundedRect(barX, barY + gaugeH - fillH, gaugeW, fillH, 3);

      // Optimal marker
      const optimalY = barY + gaugeH - (cfg.optimal / cfg.max) * gaugeH;
      this.graphics.lineStyle(2, 0xffffff, 0.35);
      this.graphics.beginPath();
      this.graphics.moveTo(barX - 2, optimalY);
      this.graphics.lineTo(barX + gaugeW + 2, optimalY);
      this.graphics.strokePath();

      // Value below bar
      this.addLabel(colX + 2, barY + gaugeH + 4, `${cfg.value}${cfg.unit}`, '#ffffff', '11px');
      this.addLabel(colX + 2, barY + gaugeH + 18, `reel:${Math.round(cfg.current)}`, '#888888', '9px');

      // - button below value
      if (batch.stage !== 'HARVEST' && batch.stage !== 'DEAD') {
        this.addButton(barX + gaugeW / 2, barY + gaugeH + 34, ' - ', '#ff9800', () => {
          for (const b of gi.batches) {
            if (b.stage !== 'HARVEST' && b.stage !== 'DEAD') {
              const p: Record<string, number> = { batchId: b.id };
              p[cfg.key] = Math.max(0, cfg.value - cfg.step);
              socketManager.sendAction('CONFIGURE_GROW', p);
            }
          }
          this.refreshAfterAction();
        });
      }
    }

    return sectionTop + gaugeH + 50;
  }

  // ════════════════════════════════════════════════════════════════════
  //  Actions
  // ════════════════════════════════════════════════════════════════════
  private renderActions(
    gi: { batches: PlantBatchInfo[]; capacity: number; currentPlants: number; id: number },
    panelX: number, y: number, panelW: number,
    ghX: number, ghW: number, height: number, margin: number,
  ): void {
    const btnX = panelX + panelW / 2;
    let btnY = y;

    if (gi.currentPlants < gi.capacity) {
      this.addButton(btnX, btnY, '  Planter STANDARD x5  ', '#8bc34a', () => {
        socketManager.sendAction('PLANT_BATCH', { growRoomId: gi.id, strain: 'STANDARD', quantity: 5 });
        this.refreshAfterAction();
      });
      btnY += 34;

      this.addButton(btnX, btnY, '  Planter PREMIUM x5   ', '#ffd700', () => {
        socketManager.sendAction('PLANT_BATCH', { growRoomId: gi.id, strain: 'PREMIUM', quantity: 5 });
        this.refreshAfterAction();
      });
      btnY += 34;
    }

    const harvestBatch = gi.batches.find(b => b.stage === 'HARVEST');
    if (harvestBatch) {
      this.graphics.fillStyle(COLORS.budHarvest, 0.1);
      this.graphics.fillRoundedRect(btnX - 85, btnY - 6, 170, 30, 6);
      this.addButton(btnX, btnY, '     RECOLTER      ', '#ffd700', () => {
        // Find a stash owned by the player to send the harvest to
        fetch('/api/world/map')
          .then(r => r.json())
          .then((mapData: { buildings: { id: number; assignedRole: string | null; ownerId: string | null }[] }) => {
            const stash = mapData.buildings.find(b => b.assignedRole === 'STASH' && b.ownerId);
            if (!stash) {
              console.warn('No stash found — buy a house and assign it as stash first');
              return;
            }
            socketManager.sendAction('HARVEST_BATCH', { batchId: harvestBatch.id, stashBuildingId: stash.id });
            this.refreshAfterAction();
          });
      });
    }

    // Optimal button in greenhouse
    this.addButton(ghX + ghW / 2, height - margin - 30, '  Reglages Optimaux  ', '#5a8a5a', () => {
      for (const batch of gi.batches) {
        if (batch.stage !== 'HARVEST' && batch.stage !== 'DEAD') {
          socketManager.sendAction('CONFIGURE_GROW', {
            batchId: batch.id,
            humidity: OPTIMAL.humidity,
            temperature: OPTIMAL.temperature,
            lightHours: OPTIMAL.lightHours,
          });
        }
      }
      this.refreshAfterAction();
    });
  }

  private refreshAfterAction(): void {
    socketManager.onceActionResult(() => {
      socketManager.sendAction('ENTER_BUILDING', { buildingId: this.building.id });
    });
  }

  bindEvents(): void {
    // Re-render on resize
    this.scene.scale.on('resize', () => {
      this.destroy();
      this.graphics = this.scene.add.graphics();
      this.render();
    });
  }
}
